import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  IconButton,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import { supabase } from '../../config/supabase'
import { useAuth } from '../../hooks/useAuth'
import { useNavigate } from 'react-router-dom'

interface Claim {
  id: string
  undertaker_id?: string
  chain_id?: string
  status: 'pending' | 'approved' | 'denied'
  requested_at: string
  reviewed_at?: string
  notes?: string
  undertaker?: {
    name: string
    city: string
    phone?: string
    chain_id?: string
  }
  chain?: {
    name: string
    slug: string
  }
  claim_type?: 'location' | 'chain'
  chain_location_count?: number
}

interface UndertakerSearchResult {
  id: string
  name: string
  city: string
  address: string
  phone?: string
}

interface ChainSearchResult {
  id: string
  name: string
  slug: string
  location_count?: number
}

export default function MyClaims() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [claims, setClaims] = useState<Claim[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // New claim dialog
  const [dialogOpen, setDialogOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [claimType, setClaimType] = useState<'location' | 'chain'>('location')
  const [undertakers, setUndertakers] = useState<UndertakerSearchResult[]>([])
  const [chains, setChains] = useState<ChainSearchResult[]>([])
  const [selectedUndertaker, setSelectedUndertaker] = useState<UndertakerSearchResult | null>(null)
  const [selectedChain, setSelectedChain] = useState<ChainSearchResult | null>(null)
  const [searchLoading, setSearchLoading] = useState(false)

  useEffect(() => {
    if (user) {
      fetchClaims()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  const fetchClaims = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('claims')
        .select(`
          id,
          undertaker_id,
          chain_id,
          status,
          requested_at,
          reviewed_at,
          notes,
          undertaker:undertakers(name, city, phone, chain_id),
          chain:chains(name, slug)
        `)
        .eq('user_id', user?.id)
        .order('requested_at', { ascending: false })

      if (error) throw error
      
      // Add claim_type and location count for chain claims
      const enrichedClaims = await Promise.all((data || []).map(async (claim: any) => {
        if (claim.chain_id) {
          const { count } = await supabase
            .from('undertakers')
            .select('*', { count: 'exact', head: true })
            .eq('chain_id', claim.chain_id)
          
          return {
            ...claim,
            claim_type: 'chain' as const,
            chain_location_count: count || 0,
          }
        }
        return {
          ...claim,
          claim_type: 'location' as const,
        }
      }))
      
      setClaims(enrichedClaims as any)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const fetchUndertakers = async (searchText: string) => {
    if (!searchText || searchText.length < 2) {
      setUndertakers([])
      return
    }

    try {
      setSearchLoading(true)
      const { data, error } = await supabase
        .from('undertakers')
        .select('id, name, city, address, phone')
        .or(`name.ilike.%${searchText}%,city.ilike.%${searchText}%`)
        .limit(20)

      if (error) throw error
      setUndertakers(data || [])
    } catch (err: any) {
      console.error('Error fetching undertakers:', err)
    } finally {
      setSearchLoading(false)
    }
  }

  const fetchChains = async (searchText: string) => {
    if (!searchText || searchText.length < 2) {
      setChains([])
      return
    }

    try {
      setSearchLoading(true)
      
      // Fetch chains with location count
      const { data, error } = await supabase
        .from('chains')
        .select('id, name, slug')
        .ilike('name', `%${searchText}%`)
        .limit(20)

      if (error) throw error

      // For each chain, get location count
      const chainsWithCount = await Promise.all((data || []).map(async (chain) => {
        const { count } = await supabase
          .from('undertakers')
          .select('*', { count: 'exact', head: true })
          .eq('chain_id', chain.id)
        
        return {
          ...chain,
          location_count: count || 0,
        }
      }))

      setChains(chainsWithCount)
    } catch (err: any) {
      console.error('Error fetching chains:', err)
    } finally {
      setSearchLoading(false)
    }
  }

  const handleClaimSubmit = async () => {
    if (!user) return
    if (claimType === 'location' && !selectedUndertaker) return
    if (claimType === 'chain' && !selectedChain) return

    try {
      setSubmitting(true)
      setError(null)

      const claimData: any = {
        user_id: user.id,
        status: 'pending',
      }

      if (claimType === 'chain' && selectedChain) {
        // Check if already claimed this chain
        const existingClaim = claims.find(c => c.chain_id === selectedChain.id)
        if (existingClaim) {
          setError('Du har allerede en ansøgning for denne kæde')
          setSubmitting(false)
          return
        }
        claimData.chain_id = selectedChain.id
      } else if (claimType === 'location' && selectedUndertaker) {
        // Check if already claimed this undertaker
        const existingClaim = claims.find(c => c.undertaker_id === selectedUndertaker.id)
        if (existingClaim) {
          setError('Du har allerede en ansøgning for denne lokation')
          setSubmitting(false)
          return
        }
        claimData.undertaker_id = selectedUndertaker.id
      }

      const { error } = await supabase
        .from('claims')
        .insert([claimData])

      if (error) throw error

      // Refresh claims list
      await fetchClaims()
      
      // Close dialog and reset
      setDialogOpen(false)
      setSelectedUndertaker(null)
      setSelectedChain(null)
      setUndertakers([])
      setChains([])
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success'
      case 'denied':
        return 'error'
      default:
        return 'warning'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'approved':
        return 'Godkendt'
      case 'denied':
        return 'Afvist'
      default:
        return 'Afventer'
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Mine forretninger
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Anmod om adgang til at administrere bedemand-virksomheder
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
        >
          Tilknyt ny
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {claims.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Du har ingen tilknyttede forretninger endnu
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Klik på "Tilknyt ny" eller gå til Bedemænd-listen for at tilknytte en virksomhed
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Navn</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Detaljer</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Anmodet</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Noter</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Handlinger</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {claims.map((claim) => (
                <TableRow key={claim.id} hover>
                  <TableCell>
                    <Chip 
                      label={claim.claim_type === 'chain' ? 'KÆDE' : 'LOKATION'} 
                      size="small" 
                      color={claim.claim_type === 'chain' ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>
                    {claim.claim_type === 'chain' ? claim.chain?.name : claim.undertaker?.name}
                  </TableCell>
                  <TableCell>
                    {claim.claim_type === 'chain' 
                      ? `${claim.chain_location_count || 0} lokationer`
                      : `${claim.undertaker?.city}`
                    }
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(claim.status)}
                      color={getStatusColor(claim.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(claim.requested_at).toLocaleDateString('da-DK')}
                  </TableCell>
                  <TableCell>{claim.notes || '-'}</TableCell>
                  <TableCell align="right">
                    {claim.status === 'approved' ? (
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => {
                          if (claim.claim_type === 'chain') {
                            navigate(`/chains/edit/${claim.chain_id}`)
                          } else {
                            navigate(`/undertakers/edit/${claim.undertaker_id}`)
                          }
                        }}
                        title={claim.claim_type === 'chain' ? 'Rediger kæde' : 'Rediger lokation'}
                      >
                        <EditIcon />
                      </IconButton>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {claim.status === 'pending' ? 'Afventer godkendelse' : '-'}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* New Claim Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Tilknyt Forretning</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Søg efter den bedemand-virksomhed eller kæde du ønsker at administrere
            </Typography>
            
            {/* Toggle between chain and location */}
            <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
              <Button
                variant={claimType === 'location' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => {
                  setClaimType('location')
                  setSelectedChain(null)
                  setChains([])
                }}
              >
                Enkelt Lokation
              </Button>
              <Button
                variant={claimType === 'chain' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => {
                  setClaimType('chain')
                  setSelectedUndertaker(null)
                  setUndertakers([])
                }}
              >
                Hele Kæde
              </Button>
            </Box>
            
            {claimType === 'location' ? (
              <Autocomplete
                options={undertakers}
                getOptionLabel={(option) => `${option.name} - ${option.city}`}
                loading={searchLoading}
                value={selectedUndertaker}
                onChange={(_, newValue) => setSelectedUndertaker(newValue)}
                onInputChange={(_, newInputValue) => {
                  fetchUndertakers(newInputValue)
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Søg efter lokation"
                    placeholder="Indtast navn eller by..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box>
                      <Typography variant="body1">{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.address}, {option.city}
                      </Typography>
                    </Box>
                  </li>
                )}
                noOptionsText="Ingen resultater - prøv at søge"
              />
            ) : (
              <Autocomplete
                options={chains}
                getOptionLabel={(option) => `${option.name} (${option.location_count || 0} lokationer)`}
                loading={searchLoading}
                value={selectedChain}
                onChange={(_, newValue) => setSelectedChain(newValue)}
                onInputChange={(_, newInputValue) => {
                  fetchChains(newInputValue)
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Søg efter kæde"
                    placeholder="Indtast kædenavn..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box>
                      <Typography variant="body1">{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.location_count || 0} lokationer
                      </Typography>
                    </Box>
                  </li>
                )}
                noOptionsText="Ingen resultater - prøv at søge"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting}>
            Annuller
          </Button>
          <Button
            onClick={handleClaimSubmit}
            variant="contained"
            disabled={(claimType === 'location' && !selectedUndertaker) || (claimType === 'chain' && !selectedChain) || submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {submitting ? 'Tilknytter...' : 'Tilknyt'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

